import { Link } from "react-router-dom";
import { PMField } from "../../components/utils/PMField";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Formik, Form } from "formik";
import PMCombobox from "../../components/utils/PMCombobox";
import axios from "../../api/axios";
import { fetchCaseTypes } from "../../api/case";
import { useEffect, useState } from "react";
import { ResourceLoading } from "../../components/utils/ResourceLoading";
import { ResourceError } from "../../components/utils/ResourceError";
import { CheckIcon } from "@heroicons/react/20/solid";
import { PageHeading } from "../../components/utils/pageHeading";
import { useCompanyInfo } from "../../data/company";

/**
 * Contact page — Airon design.
 * Left column: dark contact card with phone/email/address/hours (matches ContactCTA style).
 * Right column: light card containing the Formik contact form.
 */

export function ContactForm() {
  const [itemState, setItemState] = useState({
    isLoading: false,
    error: false,
    errors: null,
    caseTypes: [],
  });

  const { caseTypes, isLoading, error } = itemState;

  useEffect(() => {
    fetchAndMapTypeOptions();
  }, []);

  async function fetchAndMapTypeOptions() {
    setItemState((prevState) => ({ ...prevState, isLoading: true }));
    return fetchCaseTypes()
      .then((res) => {
        if (res.data) {
          const array = [];
          res.data.forEach((row, i) => {
            array.push({ value: row.id, title: row.title, order: i });
          });
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            caseTypes: array,
          }));
        }
      })
      .catch(() => {
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: true,
          errors: null,
        }));
      });
  }

  function sendForm(values) {
    setItemState((prevState) => ({ ...prevState, isLoading: true }));
    const request = { contactForm: { ...values } };
    return axios.put(`/cases/contactForm`, request);
  }

  const [caseState, setCaseState] = useState({});

  const initialValues = {
    id: null,
    name: null,
    mobile: null,
    email: null,
    address: null,
    postal: null,
    place: null,
    model_name: null,
    serial_number: null,
    description: null,
    case_type_id: 0,
    submit: null,
  };

  if (isLoading) return <ResourceLoading />;
  if (error) return <ResourceError errors="" />;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        name: Yup.string().required("Du må fylle i ditt navn"),
        mobile: Yup.string().required("Du må fylle i ditt mobilnummer"),
        email: Yup.string()
          .email("Din e-post ser ikke helt riktig ut...")
          .required("Du må fylle i din e-post"),
        address: Yup.string().required("Du må fylle i din adresse"),
        postal: Yup.string().required("Du må fylle i postnummer"),
        place: Yup.string().required("Du må fylle i sted"),
        description: Yup.string().max(500),
      })}
      onSubmit={(values, helpers) => {
        try {
          toast.promise(sendForm(values), {
            loading: "Sender...",
            success: (data) => {
              if (data.status !== 200) throw new Error("Noe gikk galt");
              helpers.resetForm(initialValues);
              setItemState((prevState) => ({ ...prevState, isLoading: false }));
              setCaseState(data.data.data);
              return "Henvendelse ble sendt";
            },
            error: () => "Noe gikk feil...",
          });
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        } catch (err) {
          setTimeout(() => {
            toast.dismiss();
            toast.error("Noe gikk feil...");
          }, 1000);
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) =>
        isLoading ? (
          <ResourceLoading />
        ) : caseState.case_number ? (
          <IsSentPlaceholder case_number={caseState.case_number} />
        ) : (
          <Form>
            <div className="form-section-title">Din kontaktinformasjon</div>
            <div className="form-grid">
              <div className="form-col form-col--half">
                <PMField name="name" type="text" label="Navn" {...formik} />
              </div>
              <div className="form-col form-col--half">
                <PMField name="mobile" type="text" label="Mobil" {...formik} />
              </div>
              <div className="form-col form-col--full">
                <PMField name="email" type="text" label="E-post" {...formik} />
              </div>
            </div>

            <div className="form-section-title">Hvor holder du til?</div>
            <div className="form-grid">
              <div className="form-col form-col--full">
                <PMField name="address" type="text" label="Adresse" {...formik} />
              </div>
              <div className="form-col form-col--full">
                <PMField
                  name="address2"
                  type="text"
                  label="Adresse / Leilighetsnummer"
                  {...formik}
                />
              </div>
              <div className="form-col form-col--half">
                <PMField name="postal" type="text" label="Postnummer" {...formik} />
              </div>
              <div className="form-col form-col--half">
                <PMField name="place" type="text" label="Sted" {...formik} />
              </div>
            </div>

            <div className="form-section-title">Informasjon om ditt anlegg</div>
            <div className="form-grid">
              <div className="form-col form-col--half">
                <PMField
                  name="model_name"
                  type="text"
                  label="Modellnavn"
                  {...formik}
                />
              </div>
              <div className="form-col form-col--half">
                <PMField
                  name="serial_number"
                  type="text"
                  label="Serienummer"
                  {...formik}
                />
              </div>
            </div>

            <div className="form-section-title">Hva gjelder din henvendelse?</div>
            <div className="form-grid">
              <div className="form-col form-col--full">
                <PMCombobox
                  name="case_type_id"
                  label="Henvendelse type"
                  options={caseTypes}
                  {...formik}
                />
              </div>
              <div className="form-col form-col--full">
                <PMField
                  name="description"
                  type="text"
                  multiline
                  label="Beskrivelse"
                  {...formik}
                />
              </div>
            </div>

            <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={formik.submitForm}
                className="btn"
              >
                Send inn <span className="arr">→</span>
              </button>
            </div>
          </Form>
        )
      }
    </Formik>
  );
}

function IsSentPlaceholder({ case_number }) {
  return (
    <div
      style={{
        padding: 40,
        background: "#fff",
        border: "1px solid var(--line)",
        borderRadius: 20,
        textAlign: "center",
      }}
    >
      <div
        style={{
          margin: "0 auto 16px",
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "rgba(57, 182, 234, 0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckIcon style={{ width: 28, height: 28, color: "var(--airon)" }} />
      </div>
      <h3
        style={{
          fontFamily: "var(--display)",
          fontWeight: 500,
          fontSize: 24,
          color: "var(--ink)",
          margin: "0 0 10px",
        }}
      >
        Din henvendelse er registrert
      </h3>
      <div
        style={{
          fontFamily: "var(--mono)",
          color: "var(--airon-3)",
          letterSpacing: "0.05em",
          fontSize: 13,
          marginBottom: 14,
        }}
      >
        SAK-{case_number}
      </div>
      <p
        style={{
          color: "var(--ink-2)",
          fontSize: 15,
          lineHeight: 1.6,
          maxWidth: "42ch",
          margin: "0 auto 24px",
        }}
      >
        Du hører fra oss så snart vi har behandlet din henvendelse.
      </p>
      <Link to="/" className="btn">
        Tilbake til startsiden <span className="arr">→</span>
      </Link>
    </div>
  );
}

export default function Contact() {
  const company = useCompanyInfo();
  return (
    <>
      <PageHeading
        eyebrow="05 / Kontakt"
        title="Ta kontakt — vi hjelper deg videre."
        description="Fyll ut skjemaet for raskest mulig svar. Du kan selvfølgelig også ringe eller sende e-post — du snakker alltid med noen som vet hva vi driver med."
      />

      <section className="sec" style={{ borderTop: "none" }}>
        <div className="wrap">
          <div
            className="contact-page-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "360px 1fr",
              gap: 32,
              alignItems: "start",
            }}
          >
            {/* Left: dark contact info card */}
            <aside
              className="cta-card"
              style={{
                background: "var(--ink)",
                color: "#fff",
                borderRadius: 20,
                padding: 32,
                position: "sticky",
                top: 96,
              }}
            >
              <h5
                style={{
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "#8da5bc",
                  margin: "0 0 20px",
                }}
              >
                Direkte kontakt
              </h5>

              <ContactRow k="Telefon" v={<a href={`tel:${company.phone.tel}`} style={{ color: "#fff" }}>{company.phone.display}</a>}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92V21a1 1 0 0 1-1.1 1 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 3.2 4.1 1 1 0 0 1 4.2 3H8.3a1 1 0 0 1 1 .75 12.84 12.84 0 0 0 .7 2.8 1 1 0 0 1-.23 1.06l-1.73 1.73a16 16 0 0 0 6 6l1.73-1.73a1 1 0 0 1 1.06-.23 12.84 12.84 0 0 0 2.8.7 1 1 0 0 1 .75 1Z" />
                </svg>
              </ContactRow>

              <ContactRow k="E-post" v={<a href={`mailto:${company.email}`} style={{ color: "#fff" }}>{company.email}</a>}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
              </ContactRow>

              <ContactRow k="Besøk" v={company.address.full}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-8 8-14a8 8 0 1 0-16 0c0 6 8 14 8 14Z" />
                  <circle cx="12" cy="8" r="3" />
                </svg>
              </ContactRow>

              <ContactRow
                k="Åpningstider"
                v={
                  <span>
                    {company.hours.lines.map((h, i) => (
                      <span key={i} style={{ display: "block" }}>
                        {h.day} · {h.time}
                      </span>
                    ))}
                  </span>
                }
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <path d="M8 2v4M16 2v4M3 10h18" />
                </svg>
              </ContactRow>

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <a
                  href="https://www.facebook.com/aironventilasjonas/"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    color: "#B5C4D3",
                    fontSize: 14,
                  }}
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.258 1H2.242C1.556 1 1 1.556 1 2.242v20.016c0 .686.556 1.242 1.242 1.242h10.776v-8.713h-2.932V11.39h2.932V8.887c0-2.906 1.775-4.489 4.367-4.489 1.242 0 2.31.093 2.62.134v3.037l-1.797.001c-1.41 0-1.683.67-1.683 1.653v2.168h3.362l-.438 3.396h-2.924V23.5h5.733c.686 0 1.242-.556 1.242-1.242V2.242C23.5 1.556 22.944 1 22.258 1" />
                  </svg>
                  Følg oss på Facebook
                </a>
              </div>
            </aside>

            {/* Right: light form card */}
            <div
              style={{
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: 20,
                padding: 40,
                boxShadow: "0 10px 30px rgba(14,34,51,0.04)",
              }}
            >
              <div className="sec-tag" style={{ marginBottom: 6 }}>
                Henvendelse
              </div>
              <h2
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 500,
                  fontSize: "clamp(22px, 2.5vw, 30px)",
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  margin: "0 0 20px",
                  lineHeight: 1.15,
                }}
              >
                Opprett en sak
              </h2>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Inline styles for the form section titles and responsive layout */}
      <style>{`
        .form-section-title {
          font-family: var(--mono);
          font-size: 11px;
          color: var(--airon-3);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin: 24px 0 10px;
          padding-bottom: 10px;
          border-bottom: 1px solid var(--line);
        }
        .form-section-title:first-of-type { margin-top: 0; }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        .form-col--full { grid-column: 1 / -1; }
        @media (max-width: 720px) {
          .contact-page-grid { grid-template-columns: 1fr !important; }
          .form-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </>
  );
}

function ContactRow({ children, k, v }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "36px 1fr",
        gap: 14,
        alignItems: "flex-start",
        padding: "12px 0",
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: "rgba(57,182,234,0.14)",
          color: "var(--airon)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ width: 18, height: 18, display: "inline-block" }}>
          {children}
        </span>
      </div>
      <div>
        <div
          style={{
            fontFamily: "var(--mono)",
            fontSize: 11,
            color: "#8da5bc",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {k}
        </div>
        <div style={{ color: "#fff", fontSize: 15, marginTop: 2 }}>{v}</div>
      </div>
    </div>
  );
}
